<template>
  <div
    class="radio-input"
    :class="{ [$class('loading')]: loading }"
  >
    <input
      :id="id"
      v-model="localModelValue"
      class="radio-input__input visually-hidden"
      :class="{ [$class('disabled')]: disabled }"
      :disabled="disabled || loading"
      :name="name"
      :required="required"
      type="radio"
      :value="value"
      @change="updateVModel(value)"
    >

    <label
      class="radio-input__label"
      :class="localClassLabel"
      :for="id"
    >
      <span
        class="radio-input__label-text"
        v-html="label"
      />
    </label>
  </div>
</template>

<script>

/**
 * Vue: Radio input (radio-input)
 * -----------------------------------------------------------------------------
 * Radio input utility component.
 *
 * @param {String} [classLabel] - Text class for label.
 * @param {Boolean} [disabled] - Input is disabled.
 * @param {String} id - ID for input.
 * @param {String} label - Input label.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number|String} modelValue - Current value provided by v-model.
 * @param {String} name - Input group name.
 * @param {Boolean} [required] - Input required?
 * @param {Number|String} value - Input value.
 *
 * @emits update:model-value - Emit v-model update event.
 *
 */
export default {
  name: 'RadioInput',

  props: {
    classLabel: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
      required: true,
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Number, String],
      default: '',
      required: true,
    },
    name: {
      type: String,
      default: '',
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Number, String],
      default: '',
      required: true,
    },
  },

  emits: {
    'update:model-value': () => true,
  },

  data() {
    return {
      localModelValue: '',
    }
  },

  computed: {

    /**
     * Compute label text class.
     * @returns {String}
     */
    localClassLabel() {
      return this.classLabel ? this.classLabel : 'text-body-m-regular'
    },
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Emit update v-model event.
     * @param {Number|String} value - Update value.
     */
    updateVModel(value) {
      this.$emit('update:model-value', value)
    },
  },
}
</script>

<style lang="scss">
@import './radio-input';
</style>
